/**
 * The pinch, which on a headset with no controllers is the only button there is.
 *
 * Apple Vision Pro's WebXR input is `transient-pointer`. It is not a device that
 * is sometimes pressed — it is an `XRInputSource` that **exists only while the
 * player is pinching**. The runtime adds it to `session.inputSources` at the
 * instant the fingers meet, fires `selectstart`, carries a `targetRaySpace`
 * pointing wherever the player was looking at that moment, then fires
 * `selectend` and removes the source again. Between pinches there is no input
 * source at all, no cursor to poll and no gaze to read: Apple deliberately does
 * not hand over continuous eye tracking, because where somebody is looking is
 * private until they choose to act on it.
 *
 * That lifecycle is the whole difficulty, and it is why this is a file rather
 * than four lines inside `xrinput.ts`. Three things can go wrong and all three
 * are the kind of bug that only shows up on the one device nobody here owns:
 *
 *  1. **A source that vanishes mid-press must still report its release.** The
 *    press is what commits a teleport. If the slot holding it is simply dropped
 *    when the source disappears, the destination is never taken and the player
 *    stands still while `acting` is left true forever on an object nobody looks
 *    at again.
 *  2. **A pinch shorter than a frame must not be lost.** `selectstart` and
 *    `selectend` can both land between two `requestAnimationFrame` callbacks —
 *    at 90 Hz that is an 11 ms window and a quick tap fits inside it. So edges
 *    are counted rather than sampled, and a press that has already ended is
 *    still played out as one frame down followed by one frame up.
 *  3. **A runtime that fires no select events at all must still work.** The
 *    spec orders `inputsourceschange` before `selectstart`, but this code has
 *    never been run on visionOS. For a `transient-pointer` the *existence* of
 *    the source is itself the press, so presence is the fallback and the events
 *    only ever refine it. For anything else presence means nothing and the
 *    events are required, or a persistent hand-tracking source with no gamepad
 *    would read as permanently pressed.
 *
 * Nothing in here knows about three, the rig, or the game. It answers one
 * question — "is the primary action down this frame?" — for one input source.
 */

/**
 * Cap on unconsumed edges.
 *
 * Edges are played out one per frame so that a double tap between two frames
 * becomes two presses rather than one. The cap stops a runtime that spams
 * events while the page is throttled from queuing up a second of phantom
 * pinches to replay when it comes back.
 */
const MAX_EDGES = 3;

/**
 * The primary action of one input source that has no button to poll.
 *
 * One of these lives on each input slot for the whole session and is reset when
 * the slot binds a new source, so no allocation happens on a pinch.
 */
export class XRPinchSelect {
  /** Unconsumed `selectstart`s. */
  private downEdges = 0;
  /** Unconsumed `selectend`s. */
  private upEdges = 0;
  /**
   * The last state the events described, or null when this source has never
   * fired one. Null is what selects the presence fallback below.
   */
  private eventHeld: boolean | null = null;
  /**
   * True only for `transient-pointer`, where the source existing *is* the
   * press. False everywhere else: a hand or a gaze source is present all the
   * time and presence must not be mistaken for a button.
   */
  private presenceIsPress = false;

  /** Start again for a newly bound source. */
  reset(presenceIsPress: boolean): void {
    this.downEdges = 0;
    this.upEdges = 0;
    this.eventHeld = null;
    this.presenceIsPress = presenceIsPress;
  }

  /** `selectstart` arrived for this source. */
  noteStart(): void {
    if (this.downEdges < MAX_EDGES) this.downEdges++;
    this.eventHeld = true;
  }

  /** `selectend` arrived for this source. */
  noteEnd(): void {
    if (this.upEdges < MAX_EDGES) this.upEdges++;
    this.eventHeld = false;
  }

  /**
   * True while there is an edge still to play out.
   *
   * The slot asks before letting a new input source move in. A pinch that began
   * and ended between two frames leaves a press nobody has seen yet, and if the
   * *next* pinch were allowed to take the slot before that frame arrived it
   * would reset this object and the first press would simply never have
   * happened.
   */
  pending(): boolean {
    return this.downEdges > 0 || this.upEdges > 0;
  }

  /**
   * Whether the primary action should be down this frame.
   *
   * `bound` is false once the source has left `session.inputSources`; `acting`
   * is what the slot reported last frame. The order of the tests is the
   * contract:
   *
   *  - an unconsumed press wins over everything, including the source having
   *    already gone, which is how a sub-frame tap survives;
   *  - then an unconsumed release, which is how a press ends on the frame the
   *    player let go rather than whenever the source happens to be collected;
   *  - then, with no edges left to play, an unbound source is always released,
   *    which is the guarantee that nothing can strand `acting` true;
   *  - and only then the steady state, which is what the events last said, or
   *    presence for a transient pointer that never said anything.
   */
  resolve(bound: boolean, acting: boolean): boolean {
    if (!acting && this.downEdges > 0) {
      this.downEdges--;
      return true;
    }
    if (acting && this.upEdges > 0) {
      this.upEdges--;
      return false;
    }
    if (!bound) {
      // Nothing left to play out and no source to play it from.
      this.downEdges = 0;
      this.upEdges = 0;
      return false;
    }
    if (this.eventHeld !== null) return this.eventHeld;
    return this.presenceIsPress;
  }
}
